
import React from "react";
import MainLayout from "@/components/layout/MainLayout";
import { useLanguage } from "@/context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const Terms: React.FC = () => {
  const { language } = useLanguage();
  const isChineseMode = language === 'zh-TW';

  return (
    <MainLayout>
      {/* 主要容器 - 漸層背景 */}
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
        
        {/* 頁面標題區塊 */}
        <section className="pt-24 pb-12 px-4 md:px-8 lg:px-12">
          <div className="container max-w-4xl mx-auto text-center">
            {/* 主標題 */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {isChineseMode ? "服務條款" : "Terms of Service"}
            </h1>
            {/* 橘色分隔線 */}
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
            {/* 副標題說明 */}
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {isChineseMode 
                ? "使用 TruStay 平台前，請詳閱以下服務條款與使用規範"
                : "Please read the following terms and conditions before using the TruStay platform"
              }
            </p>
          </div>
        </section>

        {/* 內容區塊 */}
        <div className="container max-w-4xl mx-auto px-4 md:px-8 lg:px-12 pb-16 space-y-8">
          
          {/* 第一節：平台使用條件 */}
          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "一、平台使用條件" : "1. Conditions of Use"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode 
                    ? "當您註冊或使用 TruStay（以下簡稱「本平台」）即表示您已閱讀、理解並同意遵守本條款。若您不同意，請勿使用本平台。" 
                    : "By registering or using TruStay (hereafter referred to as 'the Platform'), you agree to abide by these Terms. If you do not agree, please do not use the Platform."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 2 */}
          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "二、帳戶註冊與安全" : "2. Account Registration & Security"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "註冊帳號時，您須提供真實且完整的資料。" : "You must provide accurate and complete information when registering an account."}
                  </p>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "請妥善保管登入憑證，若遭未經授權使用，平台不承擔任何損失責任。" : "You are responsible for maintaining the confidentiality of your login credentials. The Platform is not liable for any resulting loss."}
                  </p>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "平台有權在無預警下終止違規帳號。" : "The Platform reserves the right to suspend or terminate accounts without notice for violations."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "三、使用規範與禁止行為" : "3. Acceptable Use & Prohibited Conduct"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "不得發布誹謗、中傷、仇恨或違反法律的內容。" : "You may not post defamatory, abusive, hateful, or illegal content."}
                  </p>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "禁止侵害他人隱私或智慧財產權。" : "You may not infringe on others' privacy or intellectual property rights."}
                  </p>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "嚴禁上傳假冒資料、垃圾訊息或進行騷擾。" : "Do not upload false data, spam, or engage in harassment."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "四、使用者內容與授權" : "4. User-Generated Content & Licensing"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode 
                    ? "您所發布的內容仍屬您所有，但您同意授權本平台以全球、非獨占、免權利金、可轉授權之方式使用、公開、重製及散布該內容。" 
                    : "You retain ownership of your content but grant the Platform a worldwide, non-exclusive, royalty-free, sublicensable license to use, reproduce, and distribute your content."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "五、平台資料與免責聲明" : "5. Content Accuracy & Disclaimers"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode 
                     ? "所有租屋資訊與評價由使用者提供，本平台無法保證其正確性與完整性。使用者應自行判斷是否採信，平台不對任何因使用資訊而生之損害負責。若房東或相關第三方認為某些內容造成其名譽損害或權益受損，請聯繫我們進行處理，我們會根據情況保留調整或刪除內容的權利，惟平台不對用戶行為或資訊內容負任何法律責任，亦不涉入任何租賃契約糾紛。所有使用者在平台上發布的評論、內容與個人經驗，均應自行承擔其法律後果。本平台僅提供資訊發表與查閱功能，無義務預先審核所有內容，也不對內容之正確性、完整性或合法性提供保證。若第三人主張其權利受侵害，該爭議應由原發布者自行負責與處理。"
                    : "All rental data and reviews are submitted by users. The Platform cannot guarantee their accuracy or completeness. Users should exercise independent judgment and the Platform is not liable for any damages arising from the use of such content. If landlords or third parties believe that certain content infringes on their rights or reputation, they may contact us for review. However, the Platform assumes no legal responsibility for user-submitted content and does not intervene in rental contract disputes. Users are solely responsible for the legal consequences of any comments, content, or personal experiences they publish. The Platform merely provides a space for publishing and viewing information, and has no obligation to pre-screen all content nor guarantee its accuracy, completeness, or legality. In the event of a third-party complaint, the original poster shall be solely responsible for handling the dispute."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "六、服務中斷與平台調整" : "6. Service Interruptions & Modifications"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode 
                    ? "本平台保留隨時中止、修改、刪除或新增功能之權利，無須另行通知。對於因維護、更新或第三方服務中斷所導致之資料遺失或功能限制，平台概不負責。" 
                    : "The Platform reserves the right to suspend, modify, or remove services at any time without notice. We are not responsible for any data loss or functionality disruption due to maintenance or third-party service interruptions."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "七、年齡與監護人責任" : "7. Age Limitations & Parental Consent"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode 
                    ? "本平台歡迎所有年齡層使用，但若用戶未滿13歲，應由家長或監護人陪同與同意。監護人應負責協助未成年者妥善使用平台。" 
                    : "All ages may use the Platform. However, if under 13, parental consent and supervision are required. Guardians are responsible for ensuring safe usage by minors."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "八、智慧財產權" : "8. Intellectual Property"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode 
                    ? "TruStay 名稱、標誌、介面設計、所有平台開發成果均屬本平台所有，未經授權不得使用、複製或公開展示。" 
                    : "TruStay's name, logo, UI design, and all platform developments are owned by the Platform. Unauthorized use, reproduction, or public display is prohibited."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "九、帳號終止與資料刪除" : "9. Account Termination & Data Removal"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode 
                    ? "使用者可隨時申請刪除帳號，資料將依照隱私政策處理；平台亦有權因違規或安全疑慮中止帳號使用權限。" 
                    : "Users may request account deletion at any time, and data will be handled in accordance with our Privacy Policy. We reserve the right to suspend accounts for violations or security concerns."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "十、準據法與爭議解決" : "10. Governing Law & Jurisdiction"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode 
                    ? "本條款適用中華民國法律。如有爭議，雙方同意以台北地方法院為第一審管轄法院。" 
                    : "These Terms are governed by the laws of Taiwan. All disputes shall be submitted to the Taipei District Court as the court of first instance."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 第十一節：聯絡方式 - 調整排版避免超出框位 */}
          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "十一、聯絡我們" : "11. Contact Us"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                {/* 聯絡說明文字 */}
                <p className="text-gray-700 leading-relaxed mb-6 text-center">
                  {isChineseMode 
                    ? "若您對本條款有任何疑問或建議，請透過以下方式與我們聯繫：" 
                    : "If you have any questions or suggestions about these Terms, please contact us:"
                  }
                </p>
                {/* 電子信箱區塊 - 改善排版，使用較小字體和自動換行 */}
                <div className="bg-white rounded-lg border border-orange-200 p-4">
                  <div className="text-center">
                    <span className="font-semibold text-orange-700 text-sm md:text-base break-all">
                      customer.service@trustay.app
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default Terms;
